import { followUpKeys, followUpsModuleKeys } from './followUpKeys';
import { getFollowUpTab, matchesServerSearch } from '../../../components/followUps/utils/followUpsModuleUtils';

/**
 * Local cache updates shared by mutations (this tab's own changes) and realtime events (anyone's
 * changes). They patch the cached infinite lists in place — no refetch, so a change never costs an
 * extra API call and loaded pages / scroll position are kept.
 */

// Only follow-up list pages (activity-log pages share the 'followUps' key prefix but hold `logs`)
const isFollowUpList = (data) => Array.isArray(data?.pages) && data.pages.every((page) => Array.isArray(page?.followUps));

const withoutItem = (data, id) => ({
  ...data,
  pages: data.pages.map((page) => ({ ...page, followUps: page.followUps.filter((item) => item.id !== id) })),
});

const containsItem = (data, id) => data.pages.some((page) => page.followUps.some((item) => item.id === id));

// Insert into the first page at its due-date position (lists are shown grouped by due date anyway)
const withItemInserted = (data, followUp) => {
  const [first, ...rest] = data.pages;
  const items = [...first.followUps];
  const dueTime = new Date(followUp.due_at).getTime();
  const index = items.findIndex((item) => new Date(item.due_at).getTime() > dueTime);
  items.splice(index === -1 ? items.length : index, 0, followUp);
  return { ...data, pages: [{ ...first, followUps: items }, ...rest] };
};

const withItemReplaced = (data, followUp) => ({
  ...data,
  pages: data.pages.map((page) => ({
    ...page,
    followUps: page.followUps.map((item) => (item.id === followUp.id ? followUp : item)),
  })),
});

/**
 * Put the latest version of a follow-up wherever it belongs:
 * - Follow-ups page tab lists: moved into the tab it now belongs to (Due / Upcoming / Completed /
 *   Trash) and out of every other one; respects each list's active search.
 * - Chat Details list of its conversation: replaced, added, or removed when trashed.
 */
export const upsertFollowUpInCaches = (queryClient, followUp) => {
  if (!followUp || followUp.id == null) return;
  const now = new Date();
  const targetTab = getFollowUpTab(followUp, now);

  queryClient.getQueriesData({ queryKey: followUpsModuleKeys.lists() }).forEach(([queryKey, data]) => {
    if (!data?.pages?.length || !isFollowUpList(data)) return;
    const [, , , tab, search] = queryKey;
    const belongs = tab === targetTab && matchesServerSearch(followUp, search);
    const present = containsItem(data, followUp.id);
    if (belongs && present) queryClient.setQueryData(queryKey, withItemReplaced(data, followUp));
    else if (belongs) queryClient.setQueryData(queryKey, withItemInserted(data, followUp));
    else if (present) queryClient.setQueryData(queryKey, withoutItem(data, followUp.id));
  });

  if (followUp.conversation_id == null) return;
  queryClient
    .getQueriesData({ queryKey: followUpKeys.byConversation(followUp.conversation_id) })
    .forEach(([queryKey, data]) => {
      if (!data?.pages?.length || !isFollowUpList(data)) return;
      const search = queryKey[3]?.search || '';
      const belongs = !followUp.deleted_at && matchesServerSearch(followUp, search);
      const present = containsItem(data, followUp.id);
      if (belongs && present) queryClient.setQueryData(queryKey, withItemReplaced(data, followUp));
      else if (belongs) {
        const [first, ...rest] = data.pages;
        queryClient.setQueryData(queryKey, { ...data, pages: [{ ...first, followUps: [followUp, ...first.followUps] }, ...rest] });
      } else if (present) queryClient.setQueryData(queryKey, withoutItem(data, followUp.id));
    });
};

/** Remove a follow-up from every cached list (e.g. it no longer exists — 404). */
export const removeFollowUpFromCaches = (queryClient, followUpId) => {
  queryClient.getQueriesData({ queryKey: followUpKeys.all }).forEach(([queryKey, data]) => {
    if (isFollowUpList(data) && containsItem(data, followUpId)) queryClient.setQueryData(queryKey, withoutItem(data, followUpId));
  });
};

/** Find the cached version of a follow-up in any list. */
export const findFollowUpInCaches = (queryClient, followUpId) => {
  for (const [, data] of queryClient.getQueriesData({ queryKey: followUpKeys.all })) {
    if (!isFollowUpList(data)) continue;
    for (const page of data.pages) {
      const match = page.followUps.find((item) => item.id === followUpId);
      if (match) return match;
    }
  }
  return null;
};

// ---- Own changes ---------------------------------------------------------------------------------
// The server echoes this user's own create / edit / status / trash / restore back as a
// `followup.updated` event. Those echoes still update the lists, but must not notify the user
// (badge, sound, tab dot). Mutations record the follow-up id here for a short window.
const LOCAL_CHANGE_WINDOW_MS = 15 * 1000;
const localChanges = new Map(); // followUpId → timestamp

export const markLocalFollowUpChange = (followUpId) => {
  if (followUpId == null) return;
  const now = Date.now();
  localChanges.set(String(followUpId), now);
  localChanges.forEach((time, id) => {
    if (now - time > LOCAL_CHANGE_WINDOW_MS) localChanges.delete(id);
  });
};

export const isRecentLocalFollowUpChange = (followUpId) => {
  const time = localChanges.get(String(followUpId));
  return time != null && Date.now() - time <= LOCAL_CHANGE_WINDOW_MS;
};
